import React from 'react'

import Icon from '@/shared/components/atoms/Icon'
import { cn } from '@/shared/utils/helpers'

export interface FavoriteProps {
	// Whether the heart is filled (favorited) or outlined (not favorited)
	filled?: boolean
	// Handler for when the favorite is clicked
	onClick?: (filled: boolean) => void
	// Disabled state
	disabled?: boolean
	// Loading state - shows spinner icon
	isLoading?: boolean
	// Size of the heart
	size?: 'small' | 'medium' | 'large'
	// Custom color for the filled heart
	filledColor?: string
	// Custom color for the outlined heart
	outlinedColor?: string
	// Custom CSS class
	className?: string
	// Custom CSS variables
	cssVars?: Record<string, string>
}

export const Favorite: React.FC<FavoriteProps> = ({
	filled = false,
	onClick,
	disabled = false,
	isLoading = false,
	size = 'medium',
	filledColor,
	outlinedColor,
	className = '',
	cssVars = {},
}) => {
	const sizeConfig = {
		small: { icon: '16px', padding: 'p-1' },
		medium: { icon: '24px', padding: 'p-1.5' },
		large: { icon: '32px', padding: 'p-2' },
	} as const

	const { icon: iconSize, padding } = sizeConfig[size]

	const handleClick = () => {
		if (!disabled && !isLoading && onClick) {
			onClick(!filled)
		}
	}

	const cssVariables = {
		...cssVars,
		'--favorite-filled-color':
			filledColor ||
			(cssVars['--favorite-filled-color'] as string | undefined) ||
			cssVars['filledColor'] ||
			undefined,
		'--favorite-outlined-color':
			outlinedColor ||
			(cssVars['--favorite-outlined-color'] as string | undefined) ||
			cssVars['outlinedColor'] ||
			undefined,
	} as React.CSSProperties

	const loadingColor = filledColor || outlinedColor || 'hsl(var(--foreground))'

	const buttonClassName = cn(
		'inline-flex items-center justify-center rounded-full border border-transparent bg-transparent text-foreground transition-all duration-200 ease-out',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
		isLoading || disabled
			? 'cursor-not-allowed opacity-50'
			: 'cursor-pointer hover:opacity-80 active:scale-95',
		padding,
		className
	)

	return (
		<button
			type="button"
			className={buttonClassName}
			onClick={handleClick}
			style={cssVariables}
			disabled={disabled || isLoading}
			aria-pressed={filled}
			aria-busy={isLoading}
		>
			{isLoading ? (
				<span
					className="inline-flex animate-spin rounded-full border-2 border-white/30 border-t-current"
					style={{
						width: iconSize,
						height: iconSize,
						color: loadingColor,
					}}
					role="status"
				/>
			) : (
				<Icon
					name={filled ? 'heart-fill' : 'heart'}
					width={iconSize}
					height={iconSize}
					color={
						filled
							? 'var(--favorite-filled-color, hsl(var(--foreground)))'
							: 'var(--favorite-outlined-color, hsl(var(--content-disabled)))'
					}
				/>
			)}
		</button>
	)
}

export default Favorite
